import React from 'react';
import styled, { css, keyframes } from 'styled-components';

/* ── Button ────────────────────────────────────────────────── */
const btnBase = css`
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  padding: ${p => p.$size === 'sm' ? '7px 16px' : p.$size === 'lg' ? '14px 32px' : '10px 22px'};
  font-size: ${p => p.$size === 'sm' ? '0.82rem' : p.$size === 'lg' ? '1rem' : '0.9rem'};
  font-weight: 500; border-radius: var(--radius); border: 2px solid transparent;
  cursor: pointer; transition: var(--transition); font-family: var(--font-body);
  white-space: nowrap; width: ${p => p.$block ? '100%' : 'auto'};
  &:disabled { opacity: .55; cursor: not-allowed; transform: none !important; }
`;

export const Button = styled.button`
  ${btnBase}
  ${p => p.$variant === 'primary' && css`background: var(--primary); color: #fff; border-color: var(--primary); &:hover:not(:disabled) { background: var(--primary-dark); border-color: var(--primary-dark); transform: translateY(-1px); box-shadow: var(--shadow-md); }`}
  ${p => p.$variant === 'outline' && css`background: transparent; color: var(--primary); border-color: var(--primary); &:hover:not(:disabled) { background: var(--primary); color: #fff; }`}
  ${p => p.$variant === 'ghost'   && css`background: transparent; color: var(--gray-600); border-color: transparent; &:hover:not(:disabled) { background: var(--gray-100); }`}
  ${p => p.$variant === 'danger'  && css`background: var(--error); color: #fff; border-color: var(--error); &:hover:not(:disabled) { background: #b91c1c; }`}
  ${p => p.$variant === 'white'   && css`background: #fff; color: var(--primary); border-color: #fff; &:hover:not(:disabled) { background: var(--gray-100); }`}
  ${p => p.$variant === 'secondary' && css`background: var(--gray-100); color: var(--gray-700); border-color: var(--gray-200); &:hover:not(:disabled) { background: var(--gray-200); }`}
`;
Button.defaultProps = { $variant: 'primary' };

export const IconButton = styled.button`
  width: ${p => p.$size === 'sm' ? '32px' : '40px'};
  height: ${p => p.$size === 'sm' ? '32px' : '40px'};
  border-radius: 50%; border: 1.5px solid var(--gray-200); background: #fff;
  display: inline-flex; align-items: center; justify-content: center;
  cursor: pointer; transition: var(--transition); font-size: ${p => p.$size === 'sm' ? '0.9rem' : '1.1rem'};
  color: var(--gray-600);
  &:hover { border-color: var(--primary); color: var(--primary); }
  ${p => p.$active && css`color: var(--error); border-color: var(--error);`}
`;

/* ── Badge ─────────────────────────────────────────────────── */
const badgeColors = {
  success:  css`background:#dcfce7;color:#166534;`,
  warning:  css`background:#fef3c7;color:#92400e;`,
  error:    css`background:#fee2e2;color:#991b1b;`,
  info:     css`background:#dbeafe;color:#1e40af;`,
  gray:     css`background:var(--gray-100);color:var(--gray-600);`,
  primary:  css`background:var(--primary-100);color:var(--primary-dark);`,
  purple:   css`background:#ede9fe;color:#5b21b6;`,
};

export const Badge = styled.span`
  display: inline-flex; align-items: center; padding: 3px 10px;
  border-radius: 100px; font-size: 0.75rem; font-weight: 600;
  ${p => badgeColors[p.$color || 'gray']}
`;

export function StatusBadge({ status }) {
  const colorMap = { pending:'warning', confirmed:'info', packed:'purple', shipped:'info', out_for_delivery:'primary', delivered:'success', cancelled:'error', returned:'gray', paid:'success', failed:'error', refunded:'gray', active:'success', blocked:'error', approved:'success', rejected:'error' };
  const labels = { pending:'Pending', confirmed:'Confirmed', packed:'Packed', shipped:'Shipped', out_for_delivery:'Out for Delivery', delivered:'Delivered', cancelled:'Cancelled', returned:'Returned', paid:'Paid', failed:'Failed', refunded:'Refunded', active:'Active', blocked:'Blocked', approved:'Approved', rejected:'Rejected' };
  return <Badge $color={colorMap[status] || 'gray'}>{labels[status] || status}</Badge>;
}

/* ── Card ───────────────────────────────────────────────────── */
export const Card = styled.div`
  background: #fff; border-radius: var(--radius-lg); box-shadow: var(--shadow-sm);
  border: 1px solid var(--gray-100); overflow: hidden;
`;
export const CardBody = styled.div` padding: ${p => p.$sm ? '14px' : '20px'}; `;
export const CardHeader = styled.div`
  padding: 16px 20px; border-bottom: 1px solid var(--gray-100);
  display: flex; align-items: center; justify-content: space-between;
  h3, h4 { font-size: 1rem; margin: 0; }
`;

/* ── Form ───────────────────────────────────────────────────── */
export const FormGroup = styled.div` margin-bottom: 18px; `;
export const Label = styled.label` display: block; font-size: 0.88rem; font-weight: 500; color: var(--gray-700); margin-bottom: 6px; `;
export const Input = styled.input`
  width: 100%; padding: 10px 14px; border: 1.5px solid var(--gray-200);
  border-radius: var(--radius-sm); font-size: 0.9rem; background: #fff; color: var(--gray-800);
  transition: var(--transition); font-family: var(--font-body);
  &:focus { outline: none; border-color: var(--primary-light); box-shadow: 0 0 0 3px rgba(45,106,79,.1); }
  &::placeholder { color: var(--gray-400); }
  ${p => p.$error && css`border-color: var(--error);`}
`;
export const Select = styled.select`
  width: 100%; padding: 10px 36px 10px 14px; border: 1.5px solid var(--gray-200);
  border-radius: var(--radius-sm); font-size: 0.9rem; background: #fff; color: var(--gray-800);
  appearance: none; cursor: pointer; transition: var(--transition); font-family: var(--font-body);
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat; background-position: right 12px center;
  &:focus { outline: none; border-color: var(--primary-light); box-shadow: 0 0 0 3px rgba(45,106,79,.1); }
`;
export const Textarea = styled.textarea`
  width: 100%; padding: 10px 14px; border: 1.5px solid var(--gray-200);
  border-radius: var(--radius-sm); font-size: 0.9rem; resize: vertical; min-height: 100px;
  font-family: var(--font-body);
  &:focus { outline: none; border-color: var(--primary-light); box-shadow: 0 0 0 3px rgba(45,106,79,.1); }
`;
export const FormError = styled.p` font-size: 0.8rem; color: var(--error); margin-top: 4px; `;

/* ── Modal ──────────────────────────────────────────────────── */
const overlayAnim = keyframes`from{opacity:0}to{opacity:1}`;
const modalAnim   = keyframes`from{opacity:0;transform:translateY(20px) scale(.97)}to{opacity:1;transform:none}`;

const Overlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,.45); z-index: 500;
  display: flex; align-items: center; justify-content: center; padding: 20px;
  animation: ${overlayAnim} .2s ease;
`;
const ModalBox = styled.div`
  background: #fff; border-radius: var(--radius-xl);
  width: min(${p => p.$width || '560px'}, 100%); max-height: 90vh; overflow-y: auto;
  animation: ${modalAnim} .2s ease;
`;
const ModalHeaderEl = styled.div` padding: 20px 24px; border-bottom: 1px solid var(--gray-100); display: flex; align-items: center; justify-content: space-between; h3{margin:0;font-size:1.1rem;} `;
const ModalBodyEl   = styled.div` padding: 24px; `;
const ModalFooterEl = styled.div` padding: 16px 24px; border-top: 1px solid var(--gray-100); display: flex; gap: 10px; justify-content: flex-end; `;

export function Modal({ open, onClose, title, children, footer, width }) {
  if (!open) return null;
  return (
    <Overlay onClick={e => e.target === e.currentTarget && onClose()}>
      <ModalBox $width={width}>
        {title && <ModalHeaderEl><h3>{title}</h3><IconButton $size="sm" onClick={onClose}>✕</IconButton></ModalHeaderEl>}
        <ModalBodyEl>{children}</ModalBodyEl>
        {footer && <ModalFooterEl>{footer}</ModalFooterEl>}
      </ModalBox>
    </Overlay>
  );
}

/* ── Spinner ────────────────────────────────────────────────── */
const spinAnim = keyframes`to{transform:rotate(360deg)}`;
export const Spinner = styled.span`
  display: inline-block; width: ${p => p.$size || '20px'}; height: ${p => p.$size || '20px'};
  border: 2px solid ${p => p.$dark ? 'rgba(45,106,79,.2)' : 'rgba(255,255,255,.3)'};
  border-top-color: ${p => p.$dark ? 'var(--primary)' : '#fff'};
  border-radius: 50%; animation: ${spinAnim} .7s linear infinite;
`;

export function PageLoader() {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'300px' }}>
      <Spinner $dark $size="36px" />
    </div>
  );
}

/* ── Empty State ────────────────────────────────────────────── */
export function EmptyState({ icon = '📭', title = 'Nothing here', subtitle, action }) {
  return (
    <div style={{ textAlign:'center', padding:'60px 20px', color:'var(--gray-400)' }}>
      <div style={{ fontSize:'3rem', marginBottom:'16px' }}>{icon}</div>
      <h3 style={{ color:'var(--gray-600)', marginBottom:'8px', fontFamily:'var(--font-body)', fontWeight:600 }}>{title}</h3>
      {subtitle && <p style={{ fontSize:'0.9rem' }}>{subtitle}</p>}
      {action && <div style={{ marginTop:'20px' }}>{action}</div>}
    </div>
  );
}

/* ── Stars ──────────────────────────────────────────────────── */
export function Stars({ rating, count }) {
  const r = Math.round(rating || 0);
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:'4px' }}>
      <span style={{ color:'#f59e0b', fontSize:'0.9rem' }}>{'★'.repeat(r)}{'☆'.repeat(5-r)}</span>
      {count !== undefined && <span style={{ fontSize:'0.78rem', color:'var(--gray-400)' }}>({count})</span>}
    </span>
  );
}

/* ── Qty Control ─────────────────────────────────────────────── */
export function QtyControl({ value, onChange, min = 1, max = 99 }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
      <IconButton $size="sm" onClick={() => onChange(value - 1)} disabled={value <= min}>−</IconButton>
      <span style={{ minWidth:'28px', textAlign:'center', fontWeight:600 }}>{value}</span>
      <IconButton $size="sm" onClick={() => onChange(value + 1)} disabled={value >= max}>+</IconButton>
    </div>
  );
}

/* ── Confirm Dialog ─────────────────────────────────────────── */
export function ConfirmDialog({ open, onClose, onConfirm, title = 'Are you sure?', message, danger }) {
  return (
    <Modal open={open} onClose={onClose} title={title}
      footer={<><Button $variant="secondary" onClick={onClose}>Cancel</Button><Button $variant={danger ? 'danger' : 'primary'} onClick={() => { onConfirm(); onClose(); }}>Confirm</Button></>}>
      <p style={{ color:'var(--gray-600)' }}>{message}</p>
    </Modal>
  );
}

/* ── Section Header ─────────────────────────────────────────── */
export function SectionHeader({ title, subtitle, action }) {
  return (
    <div style={{ marginBottom:'24px' }}>
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:'12px', flexWrap:'wrap' }}>
        <div>
          <h2 style={{ fontSize:'1.5rem', marginBottom:'2px' }}>{title}</h2>
          {subtitle && <p style={{ color:'var(--gray-500)', fontSize:'0.9rem' }}>{subtitle}</p>}
        </div>
        {action}
      </div>
    </div>
  );
}

/* ── Table ──────────────────────────────────────────────────── */
export function Table({ columns, data, loading, emptyMsg = 'No data' }) {
  return (
    <div style={{ overflowX:'auto', borderRadius:'var(--radius-lg)', border:'1px solid var(--gray-100)' }}>
      <table style={{ width:'100%', borderCollapse:'collapse', background:'#fff' }}>
        <thead>
          <tr>{columns.map(c => <th key={c.key} style={{ background:'var(--gray-50)', padding:'12px 16px', textAlign:'left', fontSize:'0.78rem', fontWeight:600, color:'var(--gray-600)', textTransform:'uppercase', letterSpacing:'0.5px', borderBottom:'1px solid var(--gray-100)', whiteSpace:'nowrap' }}>{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {loading ? <tr><td colSpan={columns.length} style={{ textAlign:'center', padding:'40px', color:'var(--gray-400)' }}><Spinner $dark /></td></tr>
          : !data?.length ? <tr><td colSpan={columns.length} style={{ textAlign:'center', padding:'40px', color:'var(--gray-400)' }}>{emptyMsg}</td></tr>
          : data.map((row, i) => <tr key={row.id || i} style={{ borderBottom:'1px solid var(--gray-50)' }}>{columns.map(c => <td key={c.key} style={{ padding:'14px 16px', fontSize:'0.9rem', color:'var(--gray-700)' }}>{c.render ? c.render(row) : row[c.key]}</td>)}</tr>)}
        </tbody>
      </table>
    </div>
  );
}

/* ── Stat Card ──────────────────────────────────────────────── */
export function StatCard({ icon, label, value, color = 'green', trend }) {
  const colors = { green:'var(--primary-100)', blue:'#dbeafe', orange:'#fef3c7', red:'#fee2e2', purple:'#ede9fe' };
  return (
    <Card>
      <CardBody>
        <div style={{ width:44, height:44, borderRadius:'var(--radius)', background:colors[color], display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.3rem', marginBottom:14 }}>{icon}</div>
        <div style={{ fontFamily:'var(--font-display)', fontSize:'1.8rem', fontWeight:700, color:'var(--gray-900)', lineHeight:1 }}>{value}</div>
        <div style={{ fontSize:'0.82rem', color:'var(--gray-500)', marginTop:4 }}>{label}</div>
        {trend && <div style={{ fontSize:'0.78rem', color: trend > 0 ? 'var(--success)' : 'var(--error)', marginTop:4 }}>{trend > 0 ? '↑' : '↓'} {Math.abs(trend)}% vs last period</div>}
      </CardBody>
    </Card>
  );
}

/* ── Tabs ───────────────────────────────────────────────────── */
export function Tabs({ tabs, active, onChange }) {
  return (
    <div style={{ display:'flex', borderBottom:'2px solid var(--gray-100)', marginBottom:'24px', gap:'4px', overflowX:'auto' }}>
      {tabs.map(t => (
        <button key={t.value} onClick={() => onChange(t.value)}
          style={{ padding:'10px 20px', fontSize:'0.9rem', fontWeight:500, cursor:'pointer', border:'none', background:'none', borderBottom: active === t.value ? '2px solid var(--primary)' : '2px solid transparent', marginBottom:'-2px', color: active === t.value ? 'var(--primary)' : 'var(--gray-500)', transition:'var(--transition)', whiteSpace:'nowrap' }}>
          {t.label}
        </button>
      ))}
    </div>
  );
}
